import React, { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, CircularProgress } from '@mui/material';

interface SSOCheckerProps {
  children: React.ReactNode;
  /** Show loading state while checking SSO */
  showLoading?: boolean;
}

const SSO_SESSION_KEY = 'stokastic_sso_attempted';

/**
 * Automatically attempts silent SSO authentication on mount.
 * If the user has an active session on stokastic.com, they'll be
 * automatically logged in without seeing any login UI.
 *
 * Uses getAccessTokenSilently with iframe-based silent auth (useRefreshTokens=false).
 */
const SSOChecker: React.FC<SSOCheckerProps> = ({ children, showLoading = true }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [ssoChecked, setSsoChecked] = useState(false);
  const ssoAttempted = useRef(false);

  useEffect(() => {
    const checkSSO = async () => {
      if (isLoading) {
        return;
      }

      if (isAuthenticated) {
        setSsoChecked(true);
        return;
      }

      // Skip SSO check in mobile WebView — iframe-based silent auth is blocked
      // by WKWebView/Android WebView cross-origin iframe restrictions
      if (typeof window !== 'undefined' && window.ReactNativeWebView) {
        setSsoChecked(true);
        return;
      }

      // Only attempt once per component lifecycle
      if (ssoAttempted.current) {
        setSsoChecked(true);
        return;
      }

      // Check sessionStorage to prevent repeated attempts across page loads
      if (sessionStorage.getItem(SSO_SESSION_KEY)) {
        setSsoChecked(true);
        return;
      }

      ssoAttempted.current = true;
      sessionStorage.setItem(SSO_SESSION_KEY, 'true');

      try {
        // With useRefreshTokens=false, this uses iframe-based silent auth
        // which checks for Auth0 session cookie without full redirect
        await getAccessTokenSilently({
          authorizationParams: {
            prompt: 'none',
          },
        });
        // Auth0 context will update isAuthenticated automatically
        // The effect will re-run and hit the isAuthenticated branch
      } catch {
        // Expected errors: login_required (no session), consent_required, etc.
        setSsoChecked(true);
      }
    };

    checkSSO();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  if (showLoading && (!ssoChecked || isLoading)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor="#000A16"
      >
        <CircularProgress sx={{ color: 'rgba(255, 255, 255, 0.70)' }} />
      </Box>
    );
  }

  return <>{children}</>;
};

export default SSOChecker;
